import { useState, useMemo } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { estadoNormalizado } from '@/utils/estadoUtils';
import ExportPdfFormacao from './components/ExportPdfFormacao';
import BalancoSocialFormacao from './components/BalancoSocialFormacao';
import RelatorioPorCarreiraFormacao from './components/RelatorioPorCarreiraFormacao';
import RelatorioPorUnidadeFormacao from './components/RelatorioPorUnidadeFormacao';

type TabKey = 'resumo' | 'funcionarios' | 'ranking' | 'areas' | 'orcamento' | 'balanco' | 'carreira' | 'unidade';

const CATEGORIA_CORES: Record<string, string> = {
  'Segurança':                'bg-rose-100 text-rose-700',
  'Competências Transversais':'bg-amber-100 text-amber-700',
  'Jurídica e Administrativa':'bg-indigo-100 text-indigo-700',
  'Informática':              'bg-sky-100 text-sky-700',
  'Gestão e Liderança':       'bg-emerald-100 text-emerald-700',
  'Técnica':                  'bg-zinc-200 text-zinc-700',
  'Ambiente':                 'bg-teal-100 text-teal-700',
  'Comunicação':              'bg-zinc-100 text-zinc-600',
  'Financeira':               'bg-yellow-100 text-yellow-700',
};

function CategoriaBadge({ cat }: { cat: string }) {
  const cls = CATEGORIA_CORES[cat] ?? 'bg-zinc-100 text-zinc-600';
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{cat}</span>;
}

function formatEuro(v: number) {
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

export default function RelatoriosFormacaoPage() {
  const { formacoes, participantes, loading } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();
  const [tab, setTab] = useState<TabKey>('resumo');
  const [searchFunc, setSearchFunc] = useState('');
  const [sortFunc, setSortFunc] = useState<'horas' | 'formacoes' | 'certificados' | 'nome'>('horas');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [showPdf, setShowPdf] = useState(false);
  const [anoOrc, setAnoOrc] = useState('2025');

  // ── Dados base ────────────────────────────────────────────────────────────────
  const formacoesComDados = useMemo(() => formacoes.map((f) => {
    const parts = participantes.filter((p) => p.formacao_id === f.id);
    return {
      ...f,
      totalPart: parts.length,
      certificados: parts.filter((p) => p.certificado_emitido).length,
      taxaCert: parts.length > 0 ? Math.round((parts.filter((p) => p.certificado_emitido).length / parts.length) * 100) : null,
    };
  }), [formacoes, participantes]);

  // ── KPIs globais ──────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const concluidas = formacoes.filter((f) => estadoNormalizado(f.estado) === 'concluida');
    const totalHorasFormadas = participantes.reduce((s, p) => {
      const f = formacoes.find((fc) => fc.id === p.formacao_id);
      return s + (Number(f?.duracao_horas) || 0);
    }, 0);
    const funcComFormacao = new Set(participantes.map((p) => p.funcionario_id)).size;
    const totalCert = participantes.filter((p) => p.certificado_emitido).length;
    const taxaCert = participantes.length > 0 ? Math.round((totalCert / participantes.length) * 100) : 0;
    const mediaHorasPorFunc = funcComFormacao > 0 ? Math.round(totalHorasFormadas / funcComFormacao) : 0;
    const taxaCobertura = funcionarios.length > 0 ? Math.round((funcComFormacao / funcionarios.length) * 100) : 0;
    const totalInvestimento = formacoes.reduce((s, f) => s + Number(f.custo || 0), 0);
    const totalOrcamento = formacoes.reduce((s, f) => s + Number(f.orcamento_previsto || 0), 0);
    return {
      totalFormacoes: formacoes.length,
      formacoesConc: concluidas.length,
      totalHorasFormadas,
      funcComFormacao,
      totalCert,
      taxaCert,
      mediaHorasPorFunc,
      taxaCobertura,
      totalParticipacoes: participantes.length,
      totalInvestimento,
      totalOrcamento,
    };
  }, [formacoes, participantes, funcionarios]);

  // ── Por funcionário ───────────────────────────────────────────────────────────
  const porFuncionario = useMemo(() => {
    return funcionarios
      .map((f) => {
        const parts = participantes.filter((p) => p.funcionario_id === f.id);
        const horas = parts.reduce((s, p) => {
          const form = formacoes.find((fc) => fc.id === p.formacao_id);
          return s + (Number(form?.duracao_horas) || 0);
        }, 0);
        const certs = parts.filter((p) => p.certificado_emitido).length;
        const ultimaFData = parts.reduce((max, p) => {
          const form = formacoes.find((fc) => fc.id === p.formacao_id);
          const d = form?.data_fim ?? form?.data_inicio ?? '';
          return d > max ? d : max;
        }, '');
        return {
          id: f.id,
          nome: f.nome,
          carreira: f.carreira ?? '—',
          unidade: (f as { unidade_organica?: string }).unidade_organica ?? '—',
          formacoes: parts.length,
          horas,
          certificados: certs,
          taxaCert: parts.length > 0 ? Math.round((certs / parts.length) * 100) : null,
          ultimaFormacao: ultimaFData || null,
        };
      })
      .filter((f) => {
        if (searchFunc === '') return true;
        return f.nome.toLowerCase().includes(searchFunc.toLowerCase()) ||
               f.carreira.toLowerCase().includes(searchFunc.toLowerCase()) ||
               f.unidade.toLowerCase().includes(searchFunc.toLowerCase());
      })
      .sort((a, b) => {
        const mult = sortDir === 'desc' ? -1 : 1;
        if (sortFunc === 'nome') return mult * a.nome.localeCompare(b.nome);
        return mult * ((a[sortFunc] as number) - (b[sortFunc] as number));
      });
  }, [funcionarios, participantes, formacoes, searchFunc, sortFunc, sortDir]);

  // ── Ranking formações ─────────────────────────────────────────────────────────
  const rankingFormacoes = useMemo(() => {
    return [...formacoesComDados]
      .filter((f) => f.totalPart > 0)
      .sort((a, b) => b.totalPart - a.totalPart);
  }, [formacoesComDados]);

  // ── Por área ──────────────────────────────────────────────────────────────────
  const porArea = useMemo(() => {
    const mapa: Record<string, { horas: number; participantes: number; acoes: number; certificados: number }> = {};
    formacoesComDados.forEach((f) => {
      const area = f.categoria ?? 'Sem Categoria';
      if (!mapa[area]) mapa[area] = { horas: 0, participantes: 0, acoes: 0, certificados: 0 };
      mapa[area].acoes += 1;
      mapa[area].horas += Number(f.duracao_horas) * f.totalPart;
      mapa[area].participantes += f.totalPart;
      mapa[area].certificados += f.certificados;
    });
    const maxPart = Math.max(...Object.values(mapa).map((v) => v.participantes), 1);
    return Object.entries(mapa)
      .sort((a, b) => b[1].participantes - a[1].participantes)
      .map(([area, val]) => ({
        area,
        ...val,
        taxaCert: val.participantes > 0 ? Math.round((val.certificados / val.participantes) * 100) : 0,
        pct: Math.round((val.participantes / maxPart) * 100),
      }));
  }, [formacoesComDados]);

  // ── Dados orçamentais ─────────────────────────────────────────────────────────
  const orcDados = useMemo(() => {
    const formacoesAno = formacoes.filter((f) => {
      const ano = (f.data_inicio ?? f.data_fim ?? '').slice(0, 4);
      return ano === anoOrc;
    });
    const totalPrev = formacoesAno.reduce((s, f) => s + Number(f.orcamento_previsto || 0), 0);
    const totalReal = formacoesAno.reduce((s, f) => s + Number(f.custo || 0), 0);
    const desvio = totalReal - totalPrev;
    const pctExec = totalPrev > 0 ? Math.round((totalReal / totalPrev) * 100) : 0;
    const concluidas = formacoesAno.filter((f) => estadoNormalizado(f.estado) === 'concluida');
    const realConc = concluidas.reduce((s, f) => s + Number(f.custo || 0), 0);

    // Por fonte financiamento
    const porFin: Record<string, { prev: number; real: number; count: number }> = {};
    formacoesAno.forEach((f) => {
      const fin = f.financiamento ?? 'Não especificado';
      if (!porFin[fin]) porFin[fin] = { prev: 0, real: 0, count: 0 };
      porFin[fin].prev += Number(f.orcamento_previsto || 0);
      porFin[fin].real += Number(f.custo || 0);
      porFin[fin].count += 1;
    });

    return {
      formacoes: formacoesAno,
      totalPrev,
      totalReal,
      desvio,
      pctExec,
      realConc,
      prevRestante: totalPrev - realConc,
      porFin: Object.entries(porFin).sort((a, b) => b[1].real - a[1].real),
    };
  }, [formacoes, anoOrc]);

  function toggleSort(col: typeof sortFunc) {
    if (sortFunc === col) setSortDir((d) => d === 'desc' ? 'asc' : 'desc');
    else { setSortFunc(col); setSortDir('desc'); }
  }

  const TABS: { key: TabKey; label: string; icon: string }[] = [
    { key: 'resumo',       label: 'Resumo',            icon: 'ri-dashboard-line' },
    { key: 'funcionarios', label: 'Por Funcionário',    icon: 'ri-user-line' },
    { key: 'ranking',      label: 'Ranking',            icon: 'ri-trophy-line' },
    { key: 'areas',        label: 'Por Área',           icon: 'ri-pie-chart-2-line' },
    { key: 'unidade',      label: 'Por Unidade',        icon: 'ri-building-2-line' },
    { key: 'carreira',     label: 'Por Carreira',       icon: 'ri-briefcase-line' },
    { key: 'orcamento',    label: 'Orçamental',         icon: 'ri-money-euro-circle-line' },
    { key: 'balanco',      label: 'Balanço Social',     icon: 'ri-file-chart-line' },
  ];

  if (loading) {
    return (
      <main className="flex-1 bg-slate-50/60 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
          <p className="text-[13px] text-zinc-400">A carregar relatórios...</p>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="flex-1 bg-slate-50/60 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-bar-chart-grouped-line text-white text-lg" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Relatórios de Formação</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">
                  Dados reais · {kpis.totalParticipacoes} participações · {kpis.funcComFormacao} funcionários formados
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPdf(true)}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-pdf-line text-sm" /></div>
              Exportar PDF
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-zinc-100 px-8">
          <div className="flex items-center gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`whitespace-nowrap flex items-center gap-2 px-4 py-3.5 text-[13px] font-semibold border-b-2 transition-colors cursor-pointer flex-shrink-0 ${
                  tab === t.key
                    ? 'border-zinc-900 text-zinc-900'
                    : 'border-transparent text-zinc-400 hover:text-zinc-600'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${t.icon} text-sm`} />
                </div>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-8 py-6 space-y-5">

          {/* ── TAB: RESUMO ─────────────────────────────────────────────────────── */}
          {tab === 'resumo' && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-group-line',       value: `${kpis.funcComFormacao}/${funcionarios.length}`,  label: 'Funcionários Formados',  sub: `${kpis.taxaCobertura}% de cobertura`,         bg: 'bg-zinc-100 text-zinc-600' },
                  { icon: 'ri-time-line',         value: `${kpis.totalHorasFormadas}h`,                   label: 'Horas de Formação Total', sub: `~${kpis.mediaHorasPorFunc}h por funcionário`, bg: 'bg-amber-50 text-amber-600' },
                  { icon: 'ri-medal-line',        value: `${kpis.totalCert}`,                             label: 'Certificados Emitidos',   sub: `${kpis.taxaCert}% de taxa`,                   bg: 'bg-emerald-50 text-emerald-600' },
                  { icon: 'ri-money-euro-circle-line', value: formatEuro(kpis.totalInvestimento),          label: 'Investimento Total',      sub: `de ${formatEuro(kpis.totalOrcamento)} prev.`, bg: 'bg-zinc-100 text-zinc-600' },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4">
                    <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${s.bg}`}>
                      <i className={`${s.icon} text-lg`} />
                    </div>
                    <div>
                      <p className="text-[22px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                      <p className="text-[11px] font-semibold text-zinc-500 mt-0.5">{s.label}</p>
                      <p className="text-[10px] text-zinc-400">{s.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Taxa de cobertura */}
              <div className="bg-white rounded-xl border border-zinc-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-[14px] font-bold text-zinc-800">Taxa de Cobertura da Formação</h2>
                    <p className="text-[12px] text-zinc-400 mt-0.5">Funcionários com pelo menos uma formação registada</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[28px] font-bold text-zinc-900">{kpis.taxaCobertura}%</p>
                    <p className="text-[12px] text-zinc-400">{kpis.funcComFormacao} de {funcionarios.length}</p>
                  </div>
                </div>
                <div className="h-4 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-zinc-900 transition-all duration-700" style={{ width: `${kpis.taxaCobertura}%` }} />
                </div>
                <div className="flex items-center gap-6 mt-3">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-zinc-900 inline-block" />
                    <span className="text-[12px] text-zinc-600 font-medium">{kpis.funcComFormacao} formados</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-zinc-200 inline-block" />
                    <span className="text-[12px] text-zinc-400">{funcionarios.length - kpis.funcComFormacao} sem formação</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-zinc-100">
                    <h3 className="text-[14px] font-bold text-zinc-800">Top 5 — Mais Frequentadas</h3>
                    <p className="text-[11px] text-zinc-400 mt-0.5">Por número de participantes</p>
                  </div>
                  <div className="divide-y divide-zinc-50">
                    {rankingFormacoes.slice(0, 5).map((f, i) => (
                      <div key={f.id} className="px-5 py-3 flex items-center gap-3">
                        <span className={`w-6 h-6 flex items-center justify-center rounded-full text-[11px] font-black flex-shrink-0 ${
                          i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-zinc-300 text-zinc-700' : i === 2 ? 'bg-amber-700 text-white' : 'bg-zinc-100 text-zinc-500'
                        }`}>{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-semibold text-zinc-800 truncate">{f.nome}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <CategoriaBadge cat={f.categoria ?? ''} />
                            <span className="text-[10px] text-zinc-400">{f.taxaCert !== null ? `${f.taxaCert}% cert.` : ''}</span>
                          </div>
                        </div>
                        <span className="text-[15px] font-black text-zinc-800 flex-shrink-0">{f.totalPart}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-zinc-100">
                    <h3 className="text-[14px] font-bold text-zinc-800">Taxa de Certificação por Área</h3>
                    <p className="text-[11px] text-zinc-400 mt-0.5">Percentagem de certificados emitidos</p>
                  </div>
                  <div className="divide-y divide-zinc-50">
                    {porArea.map((a) => (
                      <div key={a.area} className="px-5 py-3 flex items-center gap-4">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-50 flex-shrink-0">
                          <span className="text-[9px] font-black text-zinc-400">{a.area.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[12px] font-semibold text-zinc-800 truncate">{a.area}</span>
                            <span className="text-[11px] text-zinc-400 flex-shrink-0 ml-2">{a.participantes} part.</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${a.taxaCert >= 95 ? 'bg-emerald-500' : a.taxaCert >= 80 ? 'bg-amber-400' : 'bg-rose-400'}`}
                                style={{ width: `${a.taxaCert}%` }}
                              />
                            </div>
                            <span className="text-[11px] font-bold text-zinc-700 w-9 text-right flex-shrink-0">{a.taxaCert}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── TAB: POR FUNCIONÁRIO ─────────────────────────────────────────────── */}
          {tab === 'funcionarios' && (
            <>
              <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap gap-2.5 items-center">
                <div className="relative flex-1 min-w-[220px]">
                  <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                    <i className="ri-search-line text-sm" />
                  </div>
                  <input
                    type="text"
                    placeholder="Pesquisar funcionário, carreira ou unidade..."
                    value={searchFunc}
                    onChange={(e) => setSearchFunc(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                  />
                </div>
                <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
                  {([
                    { k: 'horas', l: 'Horas' },
                    { k: 'formacoes', l: 'Formações' },
                    { k: 'certificados', l: 'Certificados' },
                    { k: 'nome', l: 'Nome' },
                  ] as { k: typeof sortFunc; l: string }[]).map(({ k, l }) => (
                    <button
                      key={k}
                      onClick={() => toggleSort(k)}
                      className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-md transition-all cursor-pointer ${
                        sortFunc === k ? 'bg-white text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'
                      }`}
                    >
                      {l}
                      {sortFunc === k && (
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className={`${sortDir === 'desc' ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-xs`} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <span className="text-[12px] text-zinc-400 ml-auto">
                  <span className="font-semibold text-zinc-600">{porFuncionario.length}</span> funcionários
                </span>
              </div>

              <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-100 bg-zinc-50">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide w-8">#</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Funcionário</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Unidade</th>
                      <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Ações</th>
                      <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Horas</th>
                      <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Certificados</th>
                      <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Taxa Cert.</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Última Formação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porFuncionario.map((f, i) => (
                      <tr key={f.id} className={`border-b border-zinc-50 ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}>
                        <td className="px-5 py-3 text-[12px] font-bold text-zinc-300 text-center">{i + 1}</td>
                        <td className="px-5 py-3">
                          <p className="text-[13px] font-semibold text-zinc-800">{f.nome}</p>
                          <p className="text-[11px] text-zinc-400">{f.carreira}</p>
                        </td>
                        <td className="px-5 py-3">
                          <p className="text-[12px] text-zinc-600 max-w-[180px] truncate">{f.unidade}</p>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className={`text-[14px] font-bold ${f.formacoes === 0 ? 'text-zinc-300' : 'text-zinc-800'}`}>{f.formacoes}</span>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className={`text-[13px] font-semibold ${f.horas === 0 ? 'text-zinc-300' : 'text-zinc-700'}`}>{f.horas === 0 ? '0' : `${f.horas}h`}</span>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className={`text-[13px] font-semibold ${f.certificados === 0 ? 'text-zinc-300' : 'text-emerald-600'}`}>{f.certificados}</span>
                        </td>
                        <td className="px-5 py-3">
                          {f.taxaCert !== null ? (
                            <div className="flex items-center gap-2 justify-center">
                              <div className="w-16 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${f.taxaCert === 100 ? 'bg-emerald-500' : f.taxaCert >= 70 ? 'bg-amber-400' : 'bg-rose-400'}`}
                                  style={{ width: `${f.taxaCert}%` }}
                                />
                              </div>
                              <span className="text-[11px] font-bold text-zinc-600 w-8 flex-shrink-0">{f.taxaCert}%</span>
                            </div>
                          ) : (
                            <span className="text-[12px] text-zinc-300 block text-center">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          {f.ultimaFormacao ? (
                            <span className="text-[12px] text-zinc-500">
                              {new Date(f.ultimaFormacao).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          ) : (
                            <span className="text-[12px] font-semibold text-rose-400">Sem registo</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {porFuncionario.length === 0 && (
                  <div className="py-16 flex flex-col items-center gap-2">
                    <i className="ri-search-line text-3xl text-zinc-200" />
                    <p className="text-[13px] text-zinc-400">Nenhum resultado encontrado</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── TAB: RANKING ─────────────────────────────────────────────────────── */}
          {tab === 'ranking' && (
            <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-100">
                <h3 className="text-[14px] font-bold text-zinc-800">Ranking — Formações Mais Frequentadas</h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">Ordenado por número total de participantes</p>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide w-10">#</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Formação</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Entidade</th>
                    <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Part.</th>
                    <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Cert.</th>
                    <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Taxa</th>
                    <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Horas</th>
                    <th className="px-5 py-3 text-right text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Custo</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {formacoesComDados.sort((a, b) => b.totalPart - a.totalPart).map((f, i) => (
                    <tr key={f.id} className={`border-b border-zinc-50 ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`w-7 h-7 flex items-center justify-center rounded-full text-[11px] font-black mx-auto ${
                          i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-zinc-300 text-zinc-700' : i === 2 ? 'bg-amber-700/70 text-white' : 'bg-zinc-100 text-zinc-500 text-[10px]'
                        }`}>{i + 1}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-[13px] font-semibold text-zinc-800 max-w-[220px]">{f.nome}</p>
                        <CategoriaBadge cat={f.categoria ?? ''} />
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-[12px] text-zinc-600 max-w-[160px] truncate">{f.entidade_formadora ?? '—'}</p>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`text-[16px] font-black ${f.totalPart === 0 ? 'text-zinc-300' : 'text-zinc-900'}`}>{f.totalPart}</span>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`text-[13px] font-semibold ${f.certificados === 0 ? 'text-zinc-300' : 'text-emerald-600'}`}>{f.certificados}</span>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        {f.taxaCert !== null ? (
                          <span className={`text-[13px] font-bold ${f.taxaCert === 100 ? 'text-emerald-600' : f.taxaCert >= 80 ? 'text-amber-600' : 'text-rose-500'}`}>{f.taxaCert}%</span>
                        ) : <span className="text-[12px] text-zinc-300">—</span>}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className="text-[12px] font-semibold text-zinc-600">{f.duracao_horas}h</span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-[12px] font-semibold text-zinc-600">{f.custo > 0 ? formatEuro(f.custo) : '—'}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${
                          estadoNormalizado(f.estado) === 'concluida' ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'
                        }`}>{estadoNormalizado(f.estado) === 'concluida' ? 'Concluída' : 'Planeada'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── TAB: POR ÁREA ─────────────────────────────────────────────────────── */}
          {tab === 'areas' && (
            <div className="space-y-4">
              {porArea.map((a) => {
                const coresCls = CATEGORIA_CORES[a.area] ?? 'bg-zinc-100 text-zinc-600';
                const [bgCls] = coresCls.split(' ');
                return (
                  <div key={a.area} className="bg-white rounded-xl border border-zinc-100 p-5">
                    <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${bgCls}`}>
                          <i className="ri-book-open-line text-base" />
                        </div>
                        <div>
                          <h3 className="text-[15px] font-bold text-zinc-900">{a.area}</h3>
                          <p className="text-[11px] text-zinc-400 mt-0.5">{a.acoes} ação(ões) · {a.participantes} participações</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-6">
                        {[
                          { label: 'Ações', value: a.acoes },
                          { label: 'Participantes', value: a.participantes },
                          { label: 'Certificados', value: a.certificados },
                          { label: 'Taxa Cert.', value: `${a.taxaCert}%` },
                        ].map((stat) => (
                          <div key={stat.label} className="text-center">
                            <p className="text-[18px] font-black text-zinc-900">{stat.value}</p>
                            <p className="text-[10px] text-zinc-400 font-medium">{stat.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-zinc-400 w-24 flex-shrink-0">Taxa de cert.</span>
                      <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${a.taxaCert === 100 ? 'bg-emerald-500' : a.taxaCert >= 80 ? 'bg-amber-400' : 'bg-rose-400'}`}
                          style={{ width: `${a.taxaCert}%` }}
                        />
                      </div>
                      <span className="text-[12px] font-bold text-zinc-700 w-10 text-right flex-shrink-0">{a.taxaCert}%</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-zinc-100 space-y-1.5">
                      {formacoesComDados
                        .filter((f) => f.categoria === a.area)
                        .sort((x, y) => y.totalPart - x.totalPart)
                        .map((f) => (
                          <div key={f.id} className="flex items-center gap-3 bg-zinc-50/60 rounded-lg px-3 py-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-semibold text-zinc-800 truncate">{f.nome}</p>
                              <p className="text-[10px] text-zinc-400">{f.entidade_formadora ?? '—'} · {f.duracao_horas}h</p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="text-[12px] font-semibold text-zinc-600">{f.totalPart} part.</span>
                              {f.taxaCert !== null && (
                                <span className={`text-[11px] font-bold ${f.taxaCert === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>{f.taxaCert}% cert.</span>
                              )}
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${estadoNormalizado(f.estado) === 'concluida' ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                                {estadoNormalizado(f.estado) === 'concluida' ? 'Concluída' : 'Planeada'}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── TAB: POR UNIDADE ─────────────────────────────────────────────── */}
          {tab === 'unidade' && <RelatorioPorUnidadeFormacao />}

          {/* ── TAB: POR CARREIRA ─────────────────────────────────────────────── */}
          {tab === 'carreira' && <RelatorioPorCarreiraFormacao />}

          {/* ── TAB: ORÇAMENTAL ──────────────────────────────────────────────────── */}
          {tab === 'orcamento' && (
            <>
              {/* Filtro de ano */}
              <div className="flex items-center gap-2">
                {['2025', '2026'].map((a) => (
                  <button
                    key={a}
                    onClick={() => setAnoOrc(a)}
                    className={`whitespace-nowrap px-5 py-2.5 text-[13px] font-semibold rounded-xl border-2 transition-all cursor-pointer ${
                      anoOrc === a ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-100 text-zinc-500 bg-white hover:border-zinc-300'
                    }`}
                  >
                    {a}
                  </button>
                ))}
                <span className="text-[12px] text-zinc-400 ml-2">{orcDados.formacoes.length} ações</span>
              </div>

              {/* KPIs orçamentais */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-bar-chart-line', value: formatEuro(orcDados.totalPrev), label: 'Orçamento Previsto', sub: 'Total do ano', bg: 'bg-zinc-100 text-zinc-600' },
                  { icon: 'ri-money-euro-circle-line', value: formatEuro(orcDados.totalReal), label: 'Custo Executado', sub: `${orcDados.pctExec}% do previsto`, bg: orcDados.pctExec > 100 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600' },
                  { icon: 'ri-funds-line', value: (orcDados.desvio > 0 ? '+' : '') + formatEuro(orcDados.desvio), label: 'Desvio Orçamental', sub: orcDados.desvio > 0 ? 'Acima do previsto' : orcDados.desvio < 0 ? 'Abaixo do previsto' : 'Sem desvio', bg: orcDados.desvio > 0 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600' },
                  { icon: 'ri-check-double-line', value: formatEuro(orcDados.realConc), label: 'Já Executado', sub: 'Ações concluídas', bg: 'bg-zinc-100 text-zinc-600' },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4">
                    <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${s.bg}`}>
                      <i className={`${s.icon} text-lg`} />
                    </div>
                    <div>
                      <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                      <p className="text-[11px] font-semibold text-zinc-500 mt-0.5">{s.label}</p>
                      <p className="text-[10px] text-zinc-400">{s.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Barra de execução */}
              <div className="bg-white rounded-xl border border-zinc-100 p-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-[14px] font-bold text-zinc-800">Taxa de Execução Orçamental</h3>
                    <p className="text-[12px] text-zinc-400 mt-0.5">Custo real vs. orçamento previsto para {anoOrc}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-[28px] font-bold ${orcDados.pctExec > 100 ? 'text-rose-600' : 'text-zinc-900'}`}>{orcDados.pctExec}%</p>
                    <p className="text-[12px] text-zinc-400">{formatEuro(orcDados.totalReal)} / {formatEuro(orcDados.totalPrev)}</p>
                  </div>
                </div>
                <div className="h-5 bg-zinc-100 rounded-full overflow-hidden relative">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${orcDados.pctExec > 100 ? 'bg-rose-500' : orcDados.pctExec >= 80 ? 'bg-zinc-900' : 'bg-amber-500'}`}
                    style={{ width: `${Math.min(orcDados.pctExec, 100)}%` }}
                  />
                  {orcDados.pctExec > 0 && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-white">{orcDados.pctExec}%</span>
                  )}
                </div>
              </div>

              {/* Tabela por ação */}
              <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-zinc-100">
                  <h3 className="text-[14px] font-bold text-zinc-800">Detalhe por Ação de Formação</h3>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-100 bg-zinc-50">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Ação</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Financiamento</th>
                      <th className="px-5 py-3 text-right text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Previsto</th>
                      <th className="px-5 py-3 text-right text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Real</th>
                      <th className="px-5 py-3 text-right text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Desvio</th>
                      <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Exec. %</th>
                      <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orcDados.formacoes.map((f, i) => {
                      const prev = Number(f.orcamento_previsto || 0);
                      const real = Number(f.custo || 0);
                      const dev = real - prev;
                      const pct = prev > 0 ? Math.round((real / prev) * 100) : (real > 0 ? 100 : 0);
                      return (
                        <tr key={f.id} className={`border-b border-zinc-50 ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}>
                          <td className="px-5 py-3.5">
                            <p className="text-[12px] font-semibold text-zinc-800 max-w-[200px] truncate">{f.nome}</p>
                            <p className="text-[10px] text-zinc-400">{f.categoria ?? '—'}</p>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="text-[11px] text-zinc-600 max-w-[150px] truncate">{f.financiamento ?? '—'}</p>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <span className="text-[12px] text-zinc-600">{prev > 0 ? formatEuro(prev) : '—'}</span>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <span className="text-[12px] font-semibold text-zinc-800">{real > 0 ? formatEuro(real) : '—'}</span>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            {prev > 0 && real > 0 ? (
                              <span className={`text-[12px] font-bold ${dev > 0 ? 'text-rose-600' : dev < 0 ? 'text-emerald-600' : 'text-zinc-400'}`}>
                                {dev > 0 ? '+' : ''}{formatEuro(dev)}
                              </span>
                            ) : <span className="text-[12px] text-zinc-300">—</span>}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            {estadoNormalizado(f.estado) === 'concluida' && prev > 0 ? (
                              <span className={`text-[12px] font-bold ${pct > 100 ? 'text-rose-600' : pct >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>{pct}%</span>
                            ) : <span className="text-[12px] text-zinc-300">—</span>}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${estadoNormalizado(f.estado) === 'concluida' ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>
                              {estadoNormalizado(f.estado) === 'concluida' ? 'Concluída' : 'Planeada'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-zinc-50 border-t border-zinc-200">
                      <td colSpan={2} className="px-5 py-3 text-[12px] font-bold text-zinc-800">TOTAL</td>
                      <td className="px-5 py-3 text-right text-[12px] font-bold text-zinc-700">{formatEuro(orcDados.totalPrev)}</td>
                      <td className="px-5 py-3 text-right text-[12px] font-bold text-zinc-800">{formatEuro(orcDados.totalReal)}</td>
                      <td className={`px-5 py-3 text-right text-[12px] font-bold ${orcDados.desvio > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {orcDados.desvio > 0 ? '+' : ''}{formatEuro(orcDados.desvio)}
                      </td>
                      <td className={`px-5 py-3 text-center text-[13px] font-black ${orcDados.pctExec > 100 ? 'text-rose-600' : 'text-zinc-800'}`}>{orcDados.pctExec}%</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Por fonte financiamento */}
              <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-zinc-100">
                  <h3 className="text-[14px] font-bold text-zinc-800">Por Fonte de Financiamento</h3>
                </div>
                <div className="divide-y divide-zinc-50">
                  {orcDados.porFin.map(([fin, v]) => {
                    const dev = v.real - v.prev;
                    const pct = v.prev > 0 ? Math.round((v.real / v.prev) * 100) : 0;
                    return (
                      <div key={fin} className="px-5 py-4 flex items-center gap-4 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <p className="text-[13px] font-semibold text-zinc-800">{fin}</p>
                          <p className="text-[11px] text-zinc-400 mt-0.5">{v.count} ação(ões)</p>
                          {v.prev > 0 && (
                            <div className="mt-2 h-1.5 bg-zinc-100 rounded-full overflow-hidden max-w-[200px]">
                              <div
                                className={`h-full rounded-full ${pct > 100 ? 'bg-rose-500' : 'bg-zinc-900'}`}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-6 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-[11px] text-zinc-400">Previsto</p>
                            <p className="text-[13px] font-semibold text-zinc-600">{v.prev > 0 ? formatEuro(v.prev) : '—'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-[11px] text-zinc-400">Real</p>
                            <p className="text-[13px] font-bold text-zinc-800">{v.real > 0 ? formatEuro(v.real) : '—'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-[11px] text-zinc-400">Desvio</p>
                            <p className={`text-[13px] font-bold ${dev > 0 ? 'text-rose-600' : dev < 0 ? 'text-emerald-600' : 'text-zinc-400'}`}>
                              {v.prev > 0 && v.real > 0 ? (dev > 0 ? '+' : '') + formatEuro(dev) : '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
          {/* ── TAB: BALANÇO SOCIAL ──────────────────────────────────────── */}
          {tab === 'balanco' && <BalancoSocialFormacao />}

        </div>
      </main>

      {showPdf && <ExportPdfFormacao onClose={() => setShowPdf(false)} />}
    </>
  );
}
